import { Button, Form, Input, Row } from 'antd';
import React from 'react';
import * as yup from 'yup';
import './ControllerWrapper.scss';
import ControlWithOptions from '../../components/FormComponent/components/FormView/ControlWithOptions';
import ClearXIcon from 'components/Icons/ClearXIcon';

const InputSchema = yup.object().shape({
  key: yup
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(150, 'Must be at most 150 characters')
    .required('Field title is required'),
  placeholder: yup
    .string()
    .nullable()
    .transform((value) => value || null)
    .min(3, 'Must be at least 3 characters')
    .max(150, 'Must be at most 150 characters'),
});

const FormTitleSchema = yup.object().shape({
  formTitle: yup
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(300, 'Must be at most 300 characters')
    .required('Form title is required'),
});

const CancelButton = ({ onCancel }) => {
  return (
    <Row className="close-btn-wrapper" justify="end" onClick={onCancel}>
      <ClearXIcon />
    </Row>
  );
};

export const InputEditForm = ({ configText, item, onSave, onCancel, isRtl }) => {
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    onSave(values);
    onCancel(false);
  };

  // Handle Enter key to submit the form
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      form.submit(); // Manually trigger form submission on Enter
    }
  };

  const yupAddSync = {
    async validator({ field }, value) {
      await InputSchema.validateAt(field, { [field]: value });
    },
  };

  return (
    <div className={`form-edit-popover-wrapper ${isRtl ? 'language-rtl' : ''}`}>
      <CancelButton onCancel={onCancel} />
      <Form
        initialValues={{
          key: configText(item.key, true),
          placeholder: item.placeholder
            ? item.placeholder
            : `${configText('Enter', true)} ${configText(item.key, true)}`,
        }}
        layout="vertical"
        form={form}
        className="input-option-form"
        onFinish={onFinish}>
        <Form.Item
          name="key"
          rules={[yupAddSync]}
          colon={false}
          label={
            <Row justify="space-between" className="form-label">
              <span className="label">Field title</span>
            </Row>
          }>
          <Input onKeyDown={handleKeyPress} />
        </Form.Item>
        <Form.Item
          name="placeholder"
          rules={[yupAddSync]}
          colon={false}
          label={
            <Row justify="space-between" className="form-label">
              <span className="label">Placeholder text</span>
            </Row>
          }>
          <Input onKeyDown={handleKeyPress} />
        </Form.Item>
        <Row className="footer" justify="end">
          <Button className="medium-btn secondary-btn" onClick={() => form.submit()}>
            Save
          </Button>
        </Row>
      </Form>
    </div>
  );
};

export const FormTitleEditForm = ({ item, onSave, onCancel, isRtl }) => {
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    onSave(values.formTitle);
    onCancel();
  };

  // Handle Enter key to submit the form
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      form.submit(); // Manually trigger form submission on Enter
    }
  };

  const yupAddSync = {
    async validator({ field }, value) {
      await FormTitleSchema.validateAt(field, { [field]: value });
    },
  };

  return (
    <div className={`form-edit-popover-wrapper ${isRtl ? 'language-rtl' : ''}`}>
      <CancelButton onCancel={onCancel} />
      <Form
        initialValues={item}
        layout="vertical"
        form={form}
        className="input-option-form"
        onFinish={onFinish}>
        <Form.Item
          name="formTitle"
          rules={[yupAddSync]}
          colon={false}
          label={
            <Row justify="space-between" className="form-label">
              <span className="label">Form title</span>
            </Row>
          }>
          <Input onKeyDown={handleKeyPress} />
        </Form.Item>
        <Row className="footer" justify="end">
          <Button className="medium-btn secondary-btn" onClick={() => form.submit()}>
            Save
          </Button>
        </Row>
      </Form>
    </div>
  );
};

export const RadioCheckboxEditForm = ({ configText, item, onSave, onCancel, isRtl }) => {
  return (
    <div className={`form-edit-popover-wrapper ${isRtl ? 'language-rtl' : ''}`}>
      <CancelButton onCancel={onCancel} />
      <ControlWithOptions
        item={item}
        isRadio={item.type === 'radio'}
        configText={configText}
        isRTL={isRtl}
        onChange={(value) => {
          onSave(value);
          onCancel();
        }}
      />
    </div>
  );
};

export const EditControlForm = ({ item, configText, onSave, onCancel, isRtl }) => {
  if (item.type === 'head') {
    return <FormTitleEditForm item={item} onSave={onSave} onCancel={onCancel} />;
  }

  return ['checkbox', 'radio'].includes(item.type) ? (
    <RadioCheckboxEditForm
      item={item}
      configText={configText}
      onSave={onSave}
      onCancel={onCancel}
      isRtl={isRtl}
    />
  ) : (
    <InputEditForm
      item={item}
      configText={configText}
      onSave={onSave}
      onCancel={onCancel}
      isRtl={isRtl}
    />
  );
};
